import { useMemo, useState } from 'react';
import type { CameraPov, PovGroup } from '@studio/shared';

import { Carousel } from './Carousel';
import { FilterChips } from './FilterChips';
import { OptionCard } from './OptionCard';
import { useExamples } from '../lib/useExamples';
import { usePresetLabel, useT, type Translate } from '../lib/i18n';

interface Props {
  povs: CameraPov[];
  selected: string[];
  onChange: (ids: string[]) => void;
}

/** Camera set-ups: where the camera is and how much of the body is in frame. */
export function PovPicker({ povs, selected, onChange }: Props) {
  const tp = usePresetLabel();
  const t = useT();
  const examples = useExamples();
  const [group, setGroup] = useState<PovGroup | 'all'>('all');

  const groups = useMemo(
    () =>
      [...new Set(povs.map((p) => p.group))].map((id) => ({
        id,
        label: t(`group.${id}`),
        count: povs.filter((p) => p.group === id).length,
      })),
    [povs, t],
  );

  const visible = group === 'all' ? povs : povs.filter((p) => p.group === group);

  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);

  const toggleVisible = () => {
    const ids = visible.map((p) => p.id);
    const allOn = ids.every((id) => selected.includes(id));
    onChange(allOn ? selected.filter((id) => !ids.includes(id)) : [...new Set([...selected, ...ids])]);
  };

  return (
    <div className="stack stack--sm">
      <FilterChips groups={groups} active={group} onChange={setGroup} total={povs.length} />

      <Carousel
        count={t('shoot.selectedOf', {
          done: visible.filter((p) => selected.includes(p.id)).length,
          total: visible.length,
        })}
        action={
          <button type="button" className="btn btn--ghost btn--sm" onClick={toggleVisible}>
            {t(visible.every((p) => selected.includes(p.id)) ? 'filter.none' : 'filter.all')}
          </button>
        }
      >
        {visible.map((pov) => (
          <OptionCard
            key={pov.id}
            title={tp(`pov.${pov.id}.name`, pov.name)}
            description={tp(`pov.${pov.id}.desc`, pov.description)}
            spec={specOf(pov, t)}
            selected={selected.includes(pov.id)}
            example={examples[pov.id]}
            fallback={<Viewfinder pov={pov} />}
            onToggle={() => toggle(pov.id)}
          />
        ))}
      </Carousel>
    </div>
  );
}

/** Pulls the lens out of the camera fragment so the card can show it. */
function specOf(pov: CameraPov, t: Translate): string {
  const lens = pov.camera.match(/(\d+)mm/)?.[1];
  return [lens ? `${lens}mm` : null, pov.preferredRatio, t(pov.isCrop ? 'pov.crop' : 'pov.full')]
    .filter(Boolean)
    .join('  ·  ');
}

/** The figure and the crop this framing captures, for cards with no photo. */
function Viewfinder({ pov }: { pov: CameraPov }) {
  const crop = CROPS[pov.id] ?? (pov.isCrop ? { y: 3, h: 26 } : { y: 2, h: 50 });
  return (
    <svg viewBox="0 0 46 58" aria-hidden="true">
      <g stroke="var(--ink-3)" strokeWidth="1.4" fill="none" opacity="0.85">
        <circle cx="23" cy="12" r="4.2" />
        <path d="M23 16.2v17M16 21.5L23 19l7 2.5M23 33.2l-5 16M23 33.2l5 16" />
      </g>
      <rect
        x="5"
        y={crop.y}
        width="36"
        height={crop.h}
        rx="1"
        fill="rgba(255,255,255,0.03)"
        stroke="var(--ink-3)"
        strokeWidth="1"
        strokeDasharray="2.5 2"
      />
    </svg>
  );
}

const CROPS: Record<string, { y: number; h: number }> = {
  'three-quarter-crop': { y: 2, h: 32 },
  'bust-portrait': { y: 2, h: 19 },
  seated: { y: 2, h: 38 },
  'detail-fabric': { y: 15, h: 16 },
  'detail-neckline': { y: 8, h: 15 },
  'detail-hem': { y: 33, h: 18 },
  'high-angle': { y: 2, h: 34 },
};
